import React, { useState, useEffect, useRef } from 'react';
import { io } from "socket.io-client";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";

import QuizAdminLobby from '../components/QuizAdminLobby';
import QuizAdminInProgress from '../components/QuizAdminInProgress';
import QuizAdminResult from '../components/QuizAdminResult';
import UserLoginPage from '../components/UserLoginPage';

import quizInstanceAdminStatusContext from '../context/quizInstanceAdminStatusContext';
import QuizAdminStatus from '../enums/QuizInstanceAdminStatus';

import QuizApi from '../QuizApi';
import { syncQuizBgColor } from '../utils/quizBgColor';

function quizInfoFromServerResponse(serverQuizInfo) 
{
  const quizInfo = Object.assign({}, serverQuizInfo, {

    endsAt: serverQuizInfo.endsAt ? new Date(serverQuizInfo.endsAt) : undefined,
    startedAt: serverQuizInfo.startedAt ? new Date(serverQuizInfo.startedAt) : undefined,

    questionList: serverQuizInfo.questionList 
      ? serverQuizInfo.questionList.sort((a, b) => a.order < b.order ? -1 : 1)
      : undefined

  });

  return quizInfo;
}

export default function QuizInstanceAdminStatusProvider(props) {

  const { quizAccessShortId, showResult } = props;

  const [forceQuizEnd, setForceQuizEnd] = useState(false);
  const isEndingQuiz = useRef(false);
  const hasAttemptedCourseSessionAutoStart = useRef(false);

  const initialQuizStatus = {
    status: QuizAdminStatus.Initial,
    quizAccessShortId: null,
    quizInfo: null,
    socket: null,

    callbacks: {

      // Child components should be able to end the quiz
      setForceQuizEnd
    }
  };

  const [quizStatus, setQuizStatus] = useState(initialQuizStatus);

  // Apply org-configured background color and persist for next load
  useEffect(() => {
    const bg = quizStatus.quizStyle
      && quizStatus.quizStyle.colorList
      && quizStatus.quizStyle.colorList[0]
      && quizStatus.quizStyle.colorList[0].backgroundCss;

    if (quizStatus.quizStyle) {
      syncQuizBgColor(bg || null);
    }
  }, [quizStatus.quizStyle]);

  // ===============================
  // Manually end quiz if requested
  // ===============================
  useEffect(() => 
  {
    if (forceQuizEnd && !isEndingQuiz.current) 
    {
      const fn = async () => 
      {
        isEndingQuiz.current = true;

        try 
        { 
          await QuizApi.quizInstanceEnd(quizStatus.quizInstanceId); 
        }
        catch(e)
        {
          console.log(e);
        }

        isEndingQuiz.current = false;
      }

      fn();
    }

  }, [forceQuizEnd, quizStatus.quizInstanceId]);

  // ======================
  // Initial info request 
  // ======================
  useEffect(() => { 
    const start = async () => {

      try {

        if (quizStatus.status === QuizAdminStatus.Initial)
        {
          if (!quizAccessShortId) {
            throw new Error("Invalid quiz link");
          }

          setQuizStatus({ ...quizStatus, status: QuizAdminStatus.Loading });

          const apiResponse = await QuizApi.quizAdminGetInfo(quizAccessShortId);
          if (!apiResponse?.success && apiResponse?.error === "Not authenticated") {
            setQuizStatus({ ...quizStatus, status: QuizAdminStatus.ShowUserLogin });
          }
          else if (!apiResponse
            || !apiResponse?.success
            || !apiResponse?.response
            || !apiResponse?.response?.quizInfo)
          {
            throw new Error(apiResponse.error || "Invalid API response");
          }
          else if (!apiResponse.response.quizInfo.started
            && apiResponse.response.isCourseSession
            && !hasAttemptedCourseSessionAutoStart.current)
          {
            // This quiz's group has an active kurstillfälle — skip the lobby
            // (QR/link, manual "samlat läge" toggle) entirely: start it right
            // away, with roomMode decided by the fixed rule (musicquiz = ja,
            // annars nej) instead of an admin click. Re-running this same
            // effect (via status back to Initial) re-fetches quizInfo, which
            // will now come back started — rendering QuizAdminInProgress
            // exactly as if the admin had clicked through the lobby normally.
            // Only attempted once per mount — if starting fails for some
            // reason, fall through to the normal lobby on the next fetch
            // rather than retrying forever.
            hasAttemptedCourseSessionAutoStart.current = true;
            const roomMode = apiResponse.response.quizInfo.type === "Musicquiz";
            await QuizApi.quizInstanceStart(apiResponse.response.quizInstanceId, roomMode, false);
            setQuizStatus({ ...quizStatus, status: QuizAdminStatus.Initial });
          }
          else
          {
            setQuizStatus({
              ...quizStatus,

              quizInfo: quizInfoFromServerResponse(apiResponse.response.quizInfo),
              quizInstanceId: apiResponse.response.quizInstanceId,
              quizAccessId: apiResponse.response.quizAccessId,
              quizAccessShortId: apiResponse.response.quizAccessShortId,
              status: QuizAdminStatus.Loaded,

              quizUrl: apiResponse.response.quizUrl,
              quizUrlQrCode: apiResponse.response.quizUrlQrCode,
              isSuperUser: !!apiResponse.response.isSuperUser,
              isCourseSession: !!apiResponse.response.isCourseSession,

              socket: io("/", { 
                path: "/socketIO",
                query: {
                  iot: apiResponse.response.quizSocketIOToken
                }
              }),

              quizStyle: apiResponse.response.quizStyle || {}

            });
          }
        }

      } catch (error) {
        console.error(error);
        setQuizStatus({
          ...quizStatus,
          status: QuizAdminStatus.Error,
          errorMessage: error.message
        });
      }
    };
    start();

  }, [quizStatus, quizAccessShortId]);

  // =========================
  // QuizInfo update handler
  // =========================
	useEffect(() => {

    const eventName = "quizInfoUpdated";
    const eventHandler = msg => 
    {
      const newQuizInfo = quizInfoFromServerResponse(msg.quizInfo);
      const mergedQuizInfo = Object.assign({}, quizStatus.quizInfo, newQuizInfo);

      setQuizStatus({
        ...quizStatus,
        quizInfo: mergedQuizInfo
      })
    };

    if (quizStatus.socket && !quizStatus.socket.hasListeners(eventName))
    {
      quizStatus.socket.on(eventName, eventHandler);
    }
    
		return () => {

      if (quizStatus.socket)
      {
        quizStatus.socket.off(eventName, eventHandler);
      }
		};

	}, [quizStatus]);

  const showJoinQuizLoader = 
       quizStatus.status === QuizAdminStatus.Initial 
    || quizStatus.status === QuizAdminStatus.Loading;

  return (
    <quizInstanceAdminStatusContext.Provider value={quizStatus}>

      {showJoinQuizLoader && (
        <div id="adminLoaderScreen">
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        </div>
      )}

      {quizStatus.status === QuizAdminStatus.ShowUserLogin && 
        <UserLoginPage 
          onAuthSuccess={() => setQuizStatus({
            ...quizStatus, 
            status: QuizAdminStatus.Initial
          })}
        />
      }

      {quizStatus.status === QuizAdminStatus.Error && (
        <Alert variant="danger">
          <h4>Error: {quizStatus.errorMessage}</h4>
        </Alert>
      )}

      {quizStatus.status === QuizAdminStatus.Loaded && !quizStatus.quizInfo.started && 
        <QuizAdminLobby />
      }

      {quizStatus.status === QuizAdminStatus.Loaded 
      && quizStatus.quizInfo.started 
      && !showResult
      && !quizStatus.quizInfo.completed
      && <QuizAdminInProgress />
      }

      {quizStatus.status === QuizAdminStatus.Loaded 
        && quizStatus.quizInfo.started 
        && (showResult || quizStatus.quizInfo.completed)
        && <QuizAdminResult />
      }

    </quizInstanceAdminStatusContext.Provider>
  );
}

